/**
 * Schema-agnostic mapping of raw edge-data sensor names → canonical energy
 * metrics.
 *
 * Different OPC servers / edge builds expose different sensor names for the same
 * physical quantity. For example, on `edge-16` electrical consumption arrives as
 * `ActualDispensingRateElectricity`, while another schema might call it
 * `ElectricalPower`. Rather than hard-coding one schema, we match each incoming
 * sensor name against alias lists so any known variant resolves to the same
 * canonical metric. To support a new OPC schema, just add its sensor name to the
 * relevant alias list here — no other code changes needed.
 *
 * Sensor subjects look like:
 *   edge-data.live.<edge>.<serial>.<sensorPath>
 * where <sensorPath> may include an intermediate group (e.g. `BU03.Humidity`).
 * We match on the LAST path segment, normalised (lower-cased, non-alphanumerics
 * stripped).
 */

export type EnergyMetric =
  | 'electrical_power'
  | 'electricity_total'
  | 'compressed_air_volume'
  | 'compressed_air_total'
  | 'compressed_air_pressure'
  | 'vacuum_volume'
  | 'vacuum_total'
  | 'vacuum_pressure'
  | 'ambient_temperature'
  | 'ambient_humidity'
  | 'machine_speed';

/** Alias lists per metric — normalised (lowercase, alphanumeric only). */
export const METRIC_ALIASES: Record<EnergyMetric, string[]> = {
  electrical_power: [
    'actualdispensingrateelectricity',
    'electricalpower',
    'power',
    'electricity',
  ],
  electricity_total: ['dispensedtotalelectricity', 'totalelectricity'],
  compressed_air_volume: [
    'actualdispensingratecompressedair',
    'compressedairvolume',
    'compressedairflow',
  ],
  compressed_air_total: ['dispensedtotalcompressedair', 'totalcompressedair'],
  compressed_air_pressure: ['compressedairpressure', 'actualpressure', 'qopcuappressure'],
  vacuum_volume: [
    'actualdispensingratevacuum',
    'vacuumvolume',
    'vacuumflow',
  ],
  vacuum_total: ['dispensedtotalvacuum', 'totalvacuum'],
  vacuum_pressure: ['vacuumpressure', 'actualvacuum', 'qopcuavpressure'],
  ambient_temperature: ['ambienttemperature', 'temperature'],
  ambient_humidity: ['humidity', 'ambienthumidity'],
  machine_speed: ['machinespeed', 'machinevelocity', 'velocity', 'speed'],
};

/** Reverse lookup: normalised alias → canonical metric. */
const ALIAS_TO_METRIC = new Map<string, EnergyMetric>();
for (const [metric, aliases] of Object.entries(METRIC_ALIASES) as [
  EnergyMetric,
  string[],
][]) {
  for (const alias of aliases) ALIAS_TO_METRIC.set(alias, metric);
}

/** Display metadata for each canonical metric (label, unit, gauge range, color). */
export interface MetricMeta {
  key: EnergyMetric;
  label: string;
  unit: string;
  color: string;
  /**
   * Known-good operating bound, when one exists (e.g. a design/spec limit).
   * Values may still exceed it — that's abnormal, not invalid — so charts and
   * gauges use it only as a soft hint and scale past it rather than clamping.
   * Omitted metrics scale purely to their live data.
   */
  min?: number;
  max?: number;
  /** Grouping for the UI (primary energy vs system metrics). */
  group: 'primary' | 'system';
}

export const METRIC_META: Record<EnergyMetric, MetricMeta> = {
  electrical_power: { key: 'electrical_power', label: 'Electrical Power', unit: 'kW', color: '#10b981', group: 'primary' },
  electricity_total: { key: 'electricity_total', label: 'Electricity (total)', unit: 'kWh', color: '#059669', group: 'system' },
  machine_speed: { key: 'machine_speed', label: 'Machine Speed', unit: 'rpm', color: '#eab308', min: 0, group: 'primary' },
  ambient_temperature: { key: 'ambient_temperature', label: 'Temperature', unit: '°C', color: '#f59e0b', min: 0, max: 80, group: 'primary' },
  ambient_humidity: { key: 'ambient_humidity', label: 'Humidity', unit: '%', color: '#8b5cf6', min: 0, max: 100, group: 'primary' },
  compressed_air_pressure: { key: 'compressed_air_pressure', label: 'Compressed Air Pressure', unit: 'kPa', color: '#1d4ed8', min: 0, max: 7000, group: 'system' },
  compressed_air_volume: { key: 'compressed_air_volume', label: 'Compressed Air Rate', unit: 'm³/min', color: '#22d3ee', group: 'system' },
  compressed_air_total: { key: 'compressed_air_total', label: 'Compressed Air (total)', unit: 'm³', color: '#0891b2', group: 'system' },
  vacuum_pressure: { key: 'vacuum_pressure', label: 'Vacuum Pressure', unit: 'kPa', color: '#6d28d9', min: -1000, max: 0, group: 'system' },
  vacuum_volume: { key: 'vacuum_volume', label: 'Vacuum Rate', unit: 'm³/min', color: '#e879f9', group: 'system' },
  vacuum_total: { key: 'vacuum_total', label: 'Vacuum (total)', unit: 'm³', color: '#9333ea', group: 'system' },
};

/** Normalise a name segment to match against aliases. */
function normalise(segment: string): string {
  return segment.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Resolve a full sensor subject (or bare sensor name) to a canonical energy
 * metric, or null if it isn't an energy sensor we display.
 *
 *   "edge-data.live.edge-16.21304...I_HI5P.BU03_Humidity"  → "ambient_humidity"
 *   "...LoadingPointElectricEnergy.ActualDispensingRateElectricity" → "electrical_power"
 */
export function mapSensorToMetric(subjectOrName: string): EnergyMetric | null {
  if (!subjectOrName) return null;
  const parts = subjectOrName.split('.');
  const last = parts[parts.length - 1];
  // Try the whole last segment first…
  const direct = ALIAS_TO_METRIC.get(normalise(last));
  if (direct) return direct;
  // …then the tail after the last underscore. edge-16 nests the metric name as
  // e.g. "BU03_Humidity" or "BU03_AmbientTemperature", so the meaningful part is
  // after the underscore.
  const us = last.lastIndexOf('_');
  if (us !== -1) {
    const tail = last.slice(us + 1);
    const byTail = ALIAS_TO_METRIC.get(normalise(tail));
    if (byTail) return byTail;
  }
  return null;
}
